import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { IdentityDto, useChangeIdentityEmail } from '@/gen/publicApi'

import { Button } from '@/components/button'
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/dialog'
import { Form } from '@/components/form'
import FormInput from '@/components/form/formInput'
import { Input } from '@/components/input'
import { Label } from '@/components/label'
import { useToast } from '@/components/toast/useToast'

interface Props {
    identity: IdentityDto
}

const emailFormSchema = z.object({
    email: z.string().email(),
})

type EmailFormValues = z.infer<typeof emailFormSchema>

export default function EmailAddress({ identity }: Props) {
    const [emailChangeDialogOpen, setEmailChangeDialogOpen] = useState(false)
    const { toast } = useToast()
    const form = useForm<EmailFormValues>({
        resolver: zodResolver(emailFormSchema),
        defaultValues: {
            email: identity.identifier,
        },
        mode: 'onBlur',
    })

    const changeEmailMutator = useChangeIdentityEmail()

    const onSubmit = (values: EmailFormValues) => {
        changeEmailMutator.mutate(values, {
            onSuccess: () => {
                toast({
                    title: 'Check your inbox',
                    description: 'Check your current email inbox to confirm the requested email change',
                })
                setEmailChangeDialogOpen(false)
            },
            onError: error => {
                console.error('Error changing email', error)
                toast({
                    variant: 'destructive',
                    title: 'Email change failed',
                    description: 'Email change failed, please try again',
                })
            },
        })
    }

    return (
        <div>
            <Label>Email address</Label>
            <div className="flex items-center space-x-2">
                <Input className="w-full" value={identity.identifier} disabled />
                <Dialog open={emailChangeDialogOpen} onOpenChange={setEmailChangeDialogOpen}>
                    <DialogTrigger asChild>
                        <span className="cursor-pointer text-xs hover:text-primary">Change</span>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Change email</DialogTitle>
                        </DialogHeader>
                        <DialogDescription>
                            If you'd like to change the email address associated with your account, we'll send you a
                            verification link to your new email address.
                        </DialogDescription>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                <FormInput
                                    control={form.control}
                                    name="email"
                                    label="The new email address you'd like to use"
                                    disabled={false}
                                />

                                <DialogFooter>
                                    <DialogClose asChild>
                                        <Button type="button" variant="outline">
                                            Cancel
                                        </Button>
                                    </DialogClose>
                                    <Button type="submit">Update</Button>
                                </DialogFooter>
                            </form>
                        </Form>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    )
}
